import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { teamAPI } from '../services/api';

type AgentStatus = 'high-performer' | 'on-track' | 'needs-attention';
type Trend = 'up' | 'down' | 'stable';

interface Agent {
  id: string;
  name: string;
  avatar: string;
  contacts: number;
  touchpointsThisWeek: number;
  avgTouchpointsWeek: number;
  activeDeals: number;
  pipelineValue: number;
  conversionRate: number;
  lastActive: number;
  trend: Trend;
  status: AgentStatus;
}

interface TeamStats {
  totalContacts: number;
  activeDeals: number;
  avgResponseTime: string;
  weeklyTouchpoints: number;
  pipelineValue: number;
  conversionRate: number;
  contactsNeedingAttention: number;
}

export default function TeamDashboard() {
  const [timeRange, setTimeRange] = useState<'week' | 'month' | 'quarter'>('week');

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['team-stats', timeRange],
    queryFn: () => teamAPI.getStats(timeRange),
  });

  const { data: agentsData, isLoading: agentsLoading } = useQuery({
    queryKey: ['team-agents', timeRange],
    queryFn: () => teamAPI.getAgents(timeRange),
  });

  const { data: risingStarsData, isLoading: risingStarsLoading } = useQuery({
    queryKey: ['team-rising-stars'],
    queryFn: () => teamAPI.getRisingStars(),
  });

  const isLoading = statsLoading || agentsLoading;

  // Transform API data to component format (API uses snake_case)
  const apiStats = stats?.data?.stats;
  const teamMetrics: TeamStats = apiStats ? {
    totalContacts: apiStats.total_contacts || 0,
    activeDeals: apiStats.active_deals || 0,
    avgResponseTime: formatResponseTime(apiStats.avg_response_time),
    weeklyTouchpoints: apiStats.weekly_touchpoints || 0,
    pipelineValue: apiStats.pipeline_value || 0,
    conversionRate: apiStats.conversion_rate || 0,
    contactsNeedingAttention: apiStats.contacts_needing_attention || 0,
  } : {
    totalContacts: 0,
    activeDeals: 0,
    avgResponseTime: 'N/A',
    weeklyTouchpoints: 0,
    pipelineValue: 0,
    conversionRate: 0,
    contactsNeedingAttention: 0,
  };

  // Transform agents data (API already calculates status and trend)
  const apiAgents = agentsData?.data?.agents;
  const agents: Agent[] = apiAgents?.map((a) => ({
    id: a.user_id,
    name: a.name,
    avatar: a.avatar_url ? a.name.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2) : getInitials(a.name),
    contacts: a.contacts_count || 0,
    touchpointsThisWeek: a.touches_this_week || 0,
    avgTouchpointsWeek: a.avg_touches_per_week || 0,
    activeDeals: a.active_deals || 0,
    pipelineValue: a.pipeline_value || 0,
    conversionRate: a.conversion_rate || 0,
    lastActive: 0,
    trend: a.trend,
    status: a.status,
  })) || [];

  const highPerformers = agents.filter(a => a.status === 'high-performer');
  const needsAttention = agents.filter(a => a.status === 'needs-attention');
  const onTrack = agents.filter(a => a.status === 'on-track');

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ondeck-600 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading team data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Team Dashboard</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Monitor team performance and identify coaching opportunities
          </p>
        </div>
        <div className="flex items-center gap-2">
          {(['week', 'month', 'quarter'] as const).map((range) => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                timeRange === range
                  ? 'bg-ondeck-100 dark:bg-ondeck-900/50 text-ondeck-700 dark:text-ondeck-400'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              {range.charAt(0).toUpperCase() + range.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Team KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3 sm:gap-4 mb-8">
        <KPICard
          label="Total Contacts"
          value={teamMetrics.totalContacts.toLocaleString()}
          icon="👥"
        />
        <KPICard
          label="Active Deals"
          value={teamMetrics.activeDeals.toString()}
          icon="🎯"
        />
        <KPICard
          label="Avg Response"
          value={teamMetrics.avgResponseTime}
          icon="⏱️"
        />
        <KPICard
          label="Weekly Touches"
          value={teamMetrics.weeklyTouchpoints.toString()}
          icon="📞"
        />
        <KPICard
          label="Pipeline Value"
          value={teamMetrics.pipelineValue > 0 ? `$${(teamMetrics.pipelineValue / 1000000).toFixed(1)}M` : '$0'}
          icon="💰"
        />
        <KPICard
          label="Conversion Rate"
          value={`${teamMetrics.conversionRate}%`}
          icon="📈"
        />
        <KPICard
          label="Need Attention"
          value={teamMetrics.contactsNeedingAttention.toString()}
          icon="⚠️"
          highlight="warning"
        />
      </div>

      {/* Empty State */}
      {agents.length === 0 && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-8 text-center mb-8">
          <span className="text-4xl mb-4 block">👥</span>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Team Members Yet</h3>
          <p className="text-gray-600 dark:text-gray-400">
            Team data will appear here once you have multiple agents on your team.
          </p>
        </div>
      )}

      {/* Agent Status Sections */}
      {agents.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-8">
          {/* Needs Attention */}
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-xl">🚨</span>
              <h2 className="font-semibold text-red-800 dark:text-red-300">Needs Coaching</h2>
              <span className="ml-auto bg-red-200 dark:bg-red-800 text-red-800 dark:text-red-200 text-xs font-medium px-2 py-0.5 rounded-full">
                {needsAttention.length}
              </span>
            </div>
            <div className="space-y-3">
              {needsAttention.length > 0 ? (
                needsAttention.map((agent) => (
                  <AgentCard key={agent.id} agent={agent} compact />
                ))
              ) : (
                <p className="text-sm text-red-600 dark:text-red-400">All agents performing well!</p>
              )}
            </div>
          </div>

          {/* On Track */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-xl">✅</span>
              <h2 className="font-semibold text-blue-800 dark:text-blue-300">On Track</h2>
              <span className="ml-auto bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200 text-xs font-medium px-2 py-0.5 rounded-full">
                {onTrack.length}
              </span>
            </div>
            <div className="space-y-3">
              {onTrack.length > 0 ? (
                onTrack.map((agent) => (
                  <AgentCard key={agent.id} agent={agent} compact />
                ))
              ) : (
                <p className="text-sm text-blue-600 dark:text-blue-400">No agents in this category</p>
              )}
            </div>
          </div>

          {/* High Performers */}
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-xl">⭐</span>
              <h2 className="font-semibold text-green-800 dark:text-green-300">High Performers</h2>
              <span className="ml-auto bg-green-200 dark:bg-green-800 text-green-800 dark:text-green-200 text-xs font-medium px-2 py-0.5 rounded-full">
                {highPerformers.length}
              </span>
            </div>
            <div className="space-y-3">
              {highPerformers.length > 0 ? (
                highPerformers.map((agent) => (
                  <AgentCard key={agent.id} agent={agent} compact />
                ))
              ) : (
                <p className="text-sm text-green-600 dark:text-green-400">No high performers yet</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Rising Stars Section */}
      {!risingStarsLoading && risingStarsData?.data?.rising_stars && risingStarsData.data.rising_stars.length > 0 && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-6 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-2xl">🌟</span>
            <div>
              <h2 className="font-semibold text-amber-800 dark:text-amber-300">Rising Stars</h2>
              <p className="text-sm text-amber-600 dark:text-amber-400">Biggest week-over-week improvement</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
            {risingStarsData.data.rising_stars.map((star, index) => (
              <RisingStarCard
                key={star.user_id}
                rank={index + 1}
                name={star.name}
                thisWeek={star.this_week}
                lastWeek={star.last_week}
                improvement={star.improvement}
                improvementPercent={star.improvement_percent}
              />
            ))}
          </div>
        </div>
      )}

      {/* Detailed Agent List */}
      {agents.length > 0 && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="font-semibold text-gray-900 dark:text-white">All Team Members</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Agent
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Contacts
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Touches/Week
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Active Deals
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Pipeline
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Conv. Rate
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Trend
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {agents.map((agent) => (
                  <tr key={agent.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-ondeck-100 dark:bg-ondeck-900 text-ondeck-600 dark:text-ondeck-400 flex items-center justify-center text-sm font-medium">
                          {agent.avatar}
                        </div>
                        <span className="font-medium text-gray-900 dark:text-white">{agent.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusBadge status={agent.status} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-gray-900 dark:text-gray-100">
                      {agent.contacts}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-2">
                        <span className="text-gray-900 dark:text-gray-100">{agent.touchpointsThisWeek}</span>
                        <span className="text-xs text-gray-400">/ {agent.avgTouchpointsWeek} avg</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-gray-900 dark:text-gray-100">
                      {agent.activeDeals}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-gray-900 dark:text-gray-100">
                      ${(agent.pipelineValue / 1000).toFixed(0)}k
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-gray-900 dark:text-gray-100">
                      {agent.conversionRate}%
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <TrendIndicator trend={agent.trend} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper functions
function getInitials(name: string): string {
  return name
    .split(' ')
    .map(part => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function formatResponseTime(hours: number): string {
  if (!hours || hours === 0) return 'N/A';
  if (hours < 1) return `${Math.round(hours * 60)} min`;
  if (hours < 24) return `${hours.toFixed(1)} hrs`;
  return `${Math.round(hours / 24)} days`;
}

function KPICard({
  label,
  value,
  icon,
  highlight,
}: {
  label: string;
  value: string;
  icon: string;
  highlight?: 'warning' | 'success';
}) {
  const bgClass = highlight === 'warning'
    ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
    : highlight === 'success'
    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
    : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700';

  return (
    <div className={`${bgClass} border rounded-xl p-4`}>
      <div className="flex items-center gap-2 mb-1">
        <span className="text-lg">{icon}</span>
        <span className="text-xs text-gray-500 dark:text-gray-400">{label}</span>
      </div>
      <p className="text-xl font-bold text-gray-900 dark:text-white">{value}</p>
    </div>
  );
}

function AgentCard({
  agent,
  compact = false,
}: {
  agent: Agent;
  compact?: boolean;
}) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-ondeck-100 dark:bg-ondeck-900 text-ondeck-600 dark:text-ondeck-400 flex items-center justify-center text-sm font-medium">
          {agent.avatar}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-gray-900 dark:text-white text-sm truncate">{agent.name}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {agent.touchpointsThisWeek} touches this week
            {agent.touchpointsThisWeek < agent.avgTouchpointsWeek && (
              <span className="text-red-500 dark:text-red-400"> (below avg)</span>
            )}
          </p>
        </div>
        <TrendIndicator trend={agent.trend} />
      </div>
      {!compact && (
        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 grid grid-cols-3 gap-2 text-center">
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Contacts</p>
            <p className="font-medium text-gray-900 dark:text-white">{agent.contacts}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Deals</p>
            <p className="font-medium text-gray-900 dark:text-white">{agent.activeDeals}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Conv.</p>
            <p className="font-medium text-gray-900 dark:text-white">{agent.conversionRate}%</p>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: AgentStatus }) {
  const styles = {
    'high-performer': 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400',
    'on-track': 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-400',
    'needs-attention': 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400',
  };

  const labels = {
    'high-performer': 'High Performer',
    'on-track': 'On Track',
    'needs-attention': 'Needs Attention',
  };

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${styles[status]}`}>
      {labels[status]}
    </span>
  );
}

function TrendIndicator({ trend }: { trend: Trend }) {
  if (trend === 'up') {
    return (
      <span className="text-green-500" title="Trending up">
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
        </svg>
      </span>
    );
  }
  if (trend === 'down') {
    return (
      <span className="text-red-500" title="Trending down">
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
        </svg>
      </span>
    );
  }
  return (
    <span className="text-gray-400" title="Stable">
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14" />
      </svg>
    </span>
  );
}

function RisingStarCard({
  rank,
  name,
  thisWeek,
  lastWeek,
  improvement,
  improvementPercent,
}: {
  rank: number;
  name: string;
  thisWeek: number;
  lastWeek: number;
  improvement: number;
  improvementPercent: number;
}) {
  const rankEmoji = rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : `#${rank}`;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-amber-200 dark:border-amber-700 shadow-sm">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-lg">{rankEmoji}</span>
        <span className="font-medium text-gray-900 dark:text-white truncate">{name}</span>
      </div>
      <div className="flex items-center justify-between text-sm">
        <div className="text-gray-500 dark:text-gray-400">
          <span className="text-gray-900 dark:text-gray-100 font-medium">{thisWeek}</span> touches
          <span className="text-xs block text-gray-400 dark:text-gray-500">vs {lastWeek} last wk</span>
        </div>
        <div className="text-right">
          <span className="text-green-600 dark:text-green-400 font-bold">+{improvement}</span>
          <span className="text-xs block text-green-500 dark:text-green-500">
            {improvementPercent > 0 ? `+${Math.round(improvementPercent)}%` : 'New!'}
          </span>
        </div>
      </div>
    </div>
  );
}
